import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, User, Phone, Mail, MapPin, Loader2, FileText, Upload, Download, Trash2 } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { STATUS_COLORS, eur, DOC_STATUSES, DOC_STATUS_COLORS } from "@/lib/constants";
import api, { formatApiErrorDetail, downloadDoc } from "@/lib/api";

export default function AdminReferralDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ statuses: [], closure_reasons: [] });
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [closureReason, setClosureReason] = useState("");
  const [dealValue, setDealValue] = useState("");
  const [commissionPct, setCommissionPct] = useState("");
  const [saving, setSaving] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docCategory, setDocCategory] = useState("Documento");
  const [docVisible, setDocVisible] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => api.get(`/admin/referrals/${id}`).then((r) => { setData(r.data); setStatus(r.data.referral.status); setDealValue(r.data.referral.deal_value || ""); setCommissionPct(r.data.referral.commission_percentage || ""); }).catch(() => navigate("/admin/segnalazioni")), [id, navigate]);
  useEffect(() => { load(); api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, [load]);

  const CLOSED = ["NON_INTERESSATO", "SEGNALAZIONE_CHIUSA"];
  const needsDeal = ["AFFARE_CONCLUSO", "COMPENSO_MATURATO"].includes(status);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { status, note: note || null };
      if (CLOSED.includes(status)) payload.closure_reason = closureReason || "altro";
      if (needsDeal && dealValue) payload.deal_value = parseFloat(dealValue);
      if (commissionPct !== "" && commissionPct != null) payload.commission_percentage = parseFloat(commissionPct);
      await api.patch(`/admin/referrals/${id}/status`, payload);
      toast.success("Stato aggiornato.");
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const removeReferral = async () => {
    if (!window.confirm(`Eliminare definitivamente la segnalazione ${data?.referral?.code}? L'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/admin/referrals/${id}`);
      toast.success("Segnalazione eliminata.");
      navigate("/admin/segnalazioni");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const uploadDoc = async (e) => {
    e.preventDefault();
    if (!docFile) { toast.error("Seleziona un file."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("category", docCategory);
      fd.append("visible_to_reporter", docVisible);
      await api.post(`/admin/referrals/${id}/documents`, fd);
      toast.success("Documento caricato.");
      setDocFile(null); setDocCategory("Documento"); setDocVisible(true);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const changeDocStatus = async (docId, status) => {
    await api.patch(`/admin/documents/${docId}`, { status });
    load();
  };

  const removeDoc = async (docId) => {
    await api.delete(`/admin/documents/${docId}`);
    toast.success("Documento rimosso.");
    load();
  };
  if (!data) return <DashboardLayout title="Segnalazione" admin><div className="text-slate-400">Caricamento…</div></DashboardLayout>;
  const r = data.referral;

  return (
    <DashboardLayout title={`Pratica ${r.code}`} admin>
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate("/admin/segnalazioni")} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Tutte le segnalazioni
        </button>
        <button onClick={removeReferral} data-testid="delete-referral-btn" className="inline-flex items-center gap-2 text-sm font-medium text-rose-600 hover:text-rose-700">
          <Trash2 className="h-4 w-4" /> Elimina
        </button>
      </div>

      {r.flagged_duplicate && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex gap-3 mb-6" data-testid="dup-flag">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">Possibile segnalazione già presente. Verifica prima dell'attribuzione definitiva.</p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Info riservate */}
        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="text-xs font-semibold uppercase text-slate-400 mb-3">Dati riservati</p>
            <div className="space-y-3 text-sm text-slate-700">
              <p className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /> {r.owner_name}</p>
              <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {r.owner_phone}</p>
              {r.owner_email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {r.owner_email}</p>}
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {r.comune}{r.zona ? ` · ${r.zona}` : ""}</p>
              <p className="text-slate-500">Proprietario informato: <strong>{r.owner_aware ? "Sì" : "No"}</strong></p>
            </div>
            {r.notes && <p className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{r.notes}</p>}
            {r.latitude != null && r.longitude != null && (
              <div className="mt-4" data-testid="admin-referral-map">
                {r.formatted_address && <p className="text-xs text-slate-500 mb-2 flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 text-[#8a6d0f] mt-0.5 shrink-0" /> {r.formatted_address}</p>}
                <iframe
                  title="Posizione immobile"
                  className="w-full h-48 rounded-lg border border-slate-200"
                  loading="lazy"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${r.longitude - 0.004}%2C${r.latitude - 0.003}%2C${r.longitude + 0.004}%2C${r.latitude + 0.003}&layer=mapnik&marker=${r.latitude}%2C${r.longitude}`}
                />
                <a href={`https://www.google.com/maps/search/?api=1&query=${r.latitude}%2C${r.longitude}`}
                  target="_blank" rel="noopener noreferrer" data-testid="admin-open-google-maps"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#8a6d0f] hover:underline">
                  <MapPin className="h-4 w-4" /> Apri in Google Maps
                </a>
              </div>
            )}
          </div>
          {data.reporter && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Segnalatore</p>
              <p className="text-sm font-medium text-slate-800">{data.reporter.name}</p>
              <p className="text-xs text-slate-400">{data.reporter.email}</p>
              <div className="mt-3 pt-3 border-t border-slate-100">
                {data.reporter.contract_version === data.current_contract_version ? (
                  <p className="text-xs text-emerald-700" data-testid="reporter-contract-status">
                    ✔ Contratto firmato ({data.reporter.contract_version}) il{" "}
                    {data.reporter.contract_accepted_at ? new Date(data.reporter.contract_accepted_at).toLocaleString("it-IT") : "—"}
                  </p>
                ) : (
                  <p className="text-xs text-amber-600" data-testid="reporter-contract-status">
                    ⚠ Contratto {data.reporter.contract_version ? `obsoleto (${data.reporter.contract_version})` : "non firmato"}
                  </p>
                )}
              </div>
            </div>
          )}
          {data.commission && (
            <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6">
              <p className="text-xs font-semibold uppercase text-[#8a6d0f] mb-2">Merito fidelizzazione</p>
              <p className="text-sm text-slate-700">Stato: <strong>{data.commission.state}</strong></p>
              {data.referral.commission_percentage && <p className="text-sm text-slate-700">Percentuale: <strong>{data.referral.commission_percentage}%</strong></p>}
              <p className="text-sm text-slate-700">Importo: <strong>{data.commission.amount != null ? eur(data.commission.amount) : "da definire"}</strong></p>
            </div>
          )}
        </div>

        {/* Gestione stato */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-semibold text-slate-900">Gestione stato</h2>
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLORS[r.status] || "bg-slate-100"}`}>{r.status_label}</span>
            </div>
            <label className="text-sm font-medium text-slate-700">Nuovo stato</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="status-select"
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none">
              {meta.statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>

            <div className="mt-4">
              <label className="text-sm font-medium text-slate-700">Percentuale merito fidelizzazione (8–12%)</label>
              <input type="number" step="0.1" min="8" max="12" value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)} data-testid="commission-pct-input"
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" placeholder="es. 10" />
              <p className="mt-1 text-xs text-slate-400">Concordata e contrattualizzata con il proprietario in fase di incarico.</p>
            </div>

            {needsDeal && (
              <div className="mt-4">
                <label className="text-sm font-medium text-slate-700">Valore dell'affare (€) — per calcolare il merito fidelizzazione</label>
                <input type="number" value={dealValue} onChange={(e) => setDealValue(e.target.value)} data-testid="deal-value-input"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" placeholder="es. 250000" />
              </div>
            )}

            {CLOSED.includes(status) && (
              <div className="mt-4">
                <label className="text-sm font-medium text-slate-700">Motivazione chiusura</label>
                <select value={closureReason} onChange={(e) => setClosureReason(e.target.value)} data-testid="closure-select"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none">
                  <option value="">Seleziona…</option>
                  {meta.closure_reasons.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
            )}

            <div className="mt-4">
              <label className="text-sm font-medium text-slate-700">Nota interna (facoltativa)</label>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="status-note"
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" />
            </div>

            <button onClick={save} disabled={saving} data-testid="status-save-btn"
              className="btn-gold mt-5 rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Aggiorna stato
            </button>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-documents-card">
            <h2 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><FileText className="h-5 w-5" /> Documenti</h2>
            <form onSubmit={uploadDoc} className="rounded-xl border border-dashed border-slate-300 p-4 mb-4 space-y-3">
              <input type="file" onChange={(e) => setDocFile(e.target.files[0])} data-testid="doc-file-input"
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-[#0A1128] file:px-4 file:py-2 file:text-white file:text-sm" />
              <div className="flex flex-col sm:flex-row gap-3">
                <input value={docCategory} onChange={(e) => setDocCategory(e.target.value)} data-testid="doc-category-input"
                  placeholder="Categoria (es. Contratto)" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none" />
                <label className="flex items-center gap-2 text-sm text-slate-600 whitespace-nowrap">
                  <input type="checkbox" checked={docVisible} onChange={(e) => setDocVisible(e.target.checked)} data-testid="doc-visible-checkbox" className="h-4 w-4 accent-[#D4AF37]" />
                  Visibile al segnalatore
                </label>
                <button disabled={uploading} data-testid="doc-upload-btn" className="btn-gold rounded-full px-5 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica
                </button>
              </div>
            </form>
            {(!data.documents || data.documents.length === 0) ? (
              <p className="text-sm text-slate-400">Nessun documento caricato.</p>
            ) : (
              <div className="space-y-2">
                {data.documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2" data-testid={`admin-doc-${d.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{d.original_filename}</p>
                      <p className="text-xs text-slate-400">{d.category}{d.visible_to_reporter ? " · visibile al segnalatore" : " · interno"}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select value={d.status} onChange={(e) => changeDocStatus(d.id, e.target.value)} data-testid={`admin-doc-status-${d.id}`}
                        className={`rounded-full px-2 py-1 text-xs font-medium border-0 ${DOC_STATUS_COLORS[d.status] || "bg-slate-100"}`}>
                        {DOC_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                      <button onClick={() => downloadDoc(d.id)} className="text-slate-400 hover:text-[#8a6d0f]" title="Apri"><Download className="h-4 w-4" /></button>
                      <button onClick={() => removeDoc(d.id)} className="text-slate-400 hover:text-rose-600" title="Rimuovi"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">Storico stati</h2>
            <div className="space-y-3">
              {data.history.map((h) => (
                <div key={h.id} className="flex items-start gap-3 text-sm">
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-[#D4AF37] shrink-0" />
                  <div>
                    <p className="font-medium text-slate-800">{meta.statuses.find((s) => s.key === h.status)?.label || h.status}</p>
                    {h.note && <p className="text-slate-500">{h.note}</p>}
                    <p className="text-xs text-slate-400">{new Date(h.created_at).toLocaleString("it-IT")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
